import * as React from "react";

import {addStyle, type StyleType} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";

import {MessageBubbleConfigContext} from "../messages/message-bubble-config.ts";

const StyledSvg = addStyle("svg");

type Props = {
    id: string;
    style?: StyleType;
};

const GuideIcon32 = (props: Props): React.ReactElement => {
    const {disablePersonalization} = React.useContext(
        MessageBubbleConfigContext,
    );
    if (disablePersonalization) {
        return <DefaultGuideIcon style={props.style} />;
    }
    switch (props.id) {
        case "baseball-cap":
            return <BaseballCapGuideIcon style={props.style} />;
        case "sunglasses":
            return <SunglassesGuideIcon style={props.style} />;
        case "fedora":
            return <FedoraGuideIcon style={props.style} />;
        case "fedora-shiny":
            return <FedoraShinyGuideIcon style={props.style} />;
        case "headphones":
            return <HeadphonesGuideIcon style={props.style} />;
        case "beanie-and-scarf":
            return <BeanieAndScarfGuideIcon style={props.style} />;
        case "crown":
            return <CrownGuideIcon style={props.style} />;
        case "bucket-hat":
            return <BucketHatGuideIcon style={props.style} />;
        case "graduation-cap":
            return <GraduationCapGuideIcon style={props.style} />;
        case "glasses":
            return <GlassesGuideIcon style={props.style} />;
        case "cowboy-hat":
            return <CowboyHatGuideIcon style={props.style} />;
        case "beret":
            return <BeretGuideIcon style={props.style} />;
        case "party-hat":
            return <PartyHatGuideIcon style={props.style} />;
        case "reindeer":
            return <ReindeerGuideIcon style={props.style} />;
        case "offline":
            return <OfflineGuideIcon style={props.style} />;
        case "look-straight-ahead":
            return <LookStraightAheadGuideIcon style={props.style} />;
        case "heart-eyes":
            return <HeartEyesGuideIcon style={props.style} />;
        case "default":
        default:
            return <DefaultGuideIcon style={props.style} />;
    }
};

const BaseballCapGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        preserveAspectRatio="xMidYMid"
        viewBox="0 2 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_14585_14702)">
            <path
                d="M12.66 11.09H18.90C22.05 11.09 24.60 13.65 24.60 16.79V26.92C24.60 28.17 23.11 28.82 22.19 27.97L21.23 27.07C20.96 26.83 20.62 26.69 20.25 26.69H8.40C7.60 26.69 6.96 26.05 6.96 25.26V16.79C6.96 13.65 9.52 11.09 12.66 11.09Z"
                fill="#DEAE93"
            />
            <path
                d="M6.96 14.96C6.96 12.17 9.23 9.90 12.02 9.90H19.59C22.38 9.90 24.65 12.17 24.65 14.96H6.96Z"
                fill="#5BA26F"
            />
            <path
                d="M17.66 11.87C16.69 11.88 15.90 12.69 15.90 13.69V13.74H15.83V13.69C15.83 12.69 15.04 11.88 14.07 11.87C14.06 11.94 14.06 12.00 14.06 12.06C14.06 12.85 14.54 13.52 15.21 13.77C15.42 13.85 15.64 13.89 15.86 13.89C16.09 13.89 16.31 13.85 16.51 13.77C17.19 13.52 17.67 12.85 17.67 12.06C17.67 12.00 17.66 11.94 17.66 11.87Z"
                fill="white"
            />
            <path
                d="M16.27 11.82C16.50 11.60 16.50 11.23 16.27 11.01C16.05 10.78 15.68 10.78 15.46 11.01C15.23 11.23 15.23 11.60 15.46 11.82C15.68 12.05 16.05 12.05 16.27 11.82Z"
                fill="white"
            />
            <path
                d="M4.87 15.02H24.65"
                stroke="#3A644B"
                strokeWidth="1.16364"
            />
            <path
                d="M14.88 9.90C14.88 9.51 15.19 9.20 15.57 9.20H16.04C16.42 9.20 16.74 9.51 16.74 9.90H14.88Z"
                fill="#3A644B"
            />
            <ellipse
                cx="19.1692"
                cy="20.5553"
                rx="3.23086"
                ry="3.23086"
                transform="rotate(-180 19.1692 20.5553)"
                fill="white"
            />
            <mask
                id="mask0_14585_14702"
                style={{maskType: "alpha"}}
                maskUnits="userSpaceOnUse"
                x="15"
                y="16"
                width="8"
                height="8"
            >
                <ellipse
                    cx="19.1691"
                    cy="20.3294"
                    rx="3.45672"
                    ry="3.4567"
                    transform="rotate(-180 19.1691 20.3294)"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask0_14585_14702)">
                <ellipse
                    cx="19.1679"
                    cy="23.5395"
                    rx="3.13625"
                    ry="3.13624"
                    transform="rotate(-180 19.1679 23.5395)"
                    fill="black"
                />
            </g>
            <ellipse
                cx="12.0698"
                cy="20.5553"
                rx="3.23086"
                ry="3.23086"
                transform="rotate(-180 12.0698 20.5553)"
                fill="white"
            />
            <mask
                id="mask1_14585_14702"
                style={{maskType: "alpha"}}
                maskUnits="userSpaceOnUse"
                x="8"
                y="16"
                width="8"
                height="8"
            >
                <ellipse
                    cx="12.0698"
                    cy="20.3294"
                    rx="3.45672"
                    ry="3.4567"
                    transform="rotate(-180 12.0698 20.3294)"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask1_14585_14702)">
                <ellipse
                    cx="12.0686"
                    cy="23.5395"
                    rx="3.13625"
                    ry="3.13624"
                    transform="rotate(-180 12.0686 23.5395)"
                    fill="black"
                />
            </g>
        </g>
        <defs>
            <clipPath id="clip0_14585_14702">
                <rect
                    width="32"
                    height="24.5333"
                    fill="white"
                    transform="translate(0 3.7334)"
                />
            </clipPath>
        </defs>
    </StyledSvg>
);

const SunglassesGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle cx="21.4179" cy="28.8578" r="6.01791" fill="white" />
        <mask
            id="mask0_14505_30514"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="15"
            y="22"
            width="13"
            height="13"
        >
            <circle cx="21.4179" cy="28.8578" r="6.01791" fill="white" />
        </mask>
        <g mask="url(#mask0_14505_30514)">
            <circle cx="16.8" cy="26.492" r="6.16" fill="black" />
        </g>
        <circle cx="34.578" cy="28.8578" r="6.01791" fill="white" />
        <mask
            id="mask1_14505_30514"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="13"
            height="13"
        >
            <circle cx="34.578" cy="28.8578" r="6.01791" fill="white" />
        </mask>
        <g mask="url(#mask1_14505_30514)">
            <circle cx="29.96" cy="26.492" r="6.16" fill="black" />
        </g>
        <path
            d="M26.47 27.15V27.15C27.35 26.90 28.29 26.90 29.17 27.15V27.15"
            stroke="black"
            strokeWidth="3.14685"
        />
        <circle
            cx="20.7277"
            cy="28.9547"
            r="4.43649"
            stroke="black"
            strokeWidth="4.55405"
        />
        <rect
            x="13"
            y="26.4297"
            width="3.35664"
            height="2.72727"
            fill="black"
        />
        <rect
            x="39.6433"
            y="26.4297"
            width="3.35664"
            height="2.72727"
            fill="black"
        />
        <circle
            cx="20.7277"
            cy="28.9547"
            r="4.43649"
            fill="black"
            stroke="black"
            strokeWidth="4.55405"
        />
        <path
            d="M24.26 23.88L14.45 29.55"
            stroke="#F4EEE2"
            strokeWidth="1.67832"
        />
        <circle
            cx="20.7277"
            cy="28.9547"
            r="6.11951"
            stroke="black"
            strokeWidth="1.18801"
        />
        <circle
            cx="35.2373"
            cy="28.9547"
            r="4.43649"
            fill="black"
            stroke="black"
            strokeWidth="4.55405"
        />
        <path
            d="M38.77 23.88L28.96 29.55"
            stroke="#F4EEE2"
            strokeWidth="1.67832"
        />
        <circle
            cx="35.2373"
            cy="28.9547"
            r="6.11951"
            stroke="black"
            strokeWidth="1.18801"
        />
    </StyledSvg>
);

const FedoraGuideIcon = ({style}: {style?: StyleType}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12.6 22.88H43.47V43.57C43.47 45.76 40.86 46.90 39.25 45.41L37.57 43.85C37.10 43.42 36.49 43.18 35.86 43.18H15.11C13.72 43.18 12.6 42.05 12.6 40.67V22.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5777"
            cy="30.859"
            r="6.01791"
            transform="rotate(-180 34.5777 30.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30708"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="24"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5776"
                cy="30.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5776 30.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30708)">
            <ellipse
                cx="34.5754"
                cy="36.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5754 36.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4176"
            cy="30.859"
            r="6.01791"
            transform="rotate(-180 21.4176 30.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30708"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="24"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4176"
                cy="30.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4176 30.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30708)">
            <ellipse
                cx="21.4154"
                cy="36.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4154 36.4181)"
                fill="black"
            />
        </g>
        <path d="M9 23.20H47" stroke="black" strokeWidth="2.11111" />
        <path d="M9 23.20H47" stroke="black" strokeWidth="2.11111" />
        <path
            d="M16.97 13.31L13.01 22.14H43.41L42.38 15.49C41.79 11.69 37.77 9.46 34.23 10.96L30.38 12.59C30.28 12.63 30.16 12.63 30.05 12.59L24.69 10.29C21.73 9.02 18.29 10.36 16.97 13.31Z"
            fill="black"
            stroke="black"
            strokeWidth="1.05556"
            strokeLinecap="square"
            strokeLinejoin="round"
        />
        <mask
            id="mask2_14505_30708"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="12"
            y="9"
            width="32"
            height="14"
        >
            <path
                d="M16.97 13.31L13.01 22.14H43.41L42.38 15.49C41.79 11.69 37.77 9.46 34.23 10.96L30.38 12.59C30.28 12.63 30.16 12.63 30.05 12.59L24.69 10.29C21.73 9.02 18.29 10.36 16.97 13.31Z"
                fill="black"
                stroke="black"
                strokeWidth="1.05556"
                strokeLinecap="square"
                strokeLinejoin="round"
            />
        </mask>
        <g mask="url(#mask2_14505_30708)">
            <rect
                x="12.1666"
                y="19.1885"
                width="32.7222"
                height="2.95556"
                fill="#F4EEE2"
            />
        </g>
    </StyledSvg>
);

const HeadphonesGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5778"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5778 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30535"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5778"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5778 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30535)">
            <ellipse
                cx="34.5756"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5756 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4178"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4178 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30535"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4177"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4177 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30535)">
            <ellipse
                cx="21.4155"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4155 34.4181)"
                fill="black"
            />
        </g>
        <path
            d="M13.02 25.45V19.54C13.02 15.93 15.95 13 19.56 13H36.44C40.05 13 42.98 15.93 42.98 19.54V25.45"
            stroke="#BED5FF"
            strokeWidth="3.37615"
        />
        <path
            d="M5 27.14C5 30.40 7.65 33.05 10.91 33.05V21.23C7.65 21.23 5 23.87 5 27.14Z"
            fill="#BED5FF"
        />
        <rect
            x="10.6973"
            y="19.9629"
            width="4.00918"
            height="14.1376"
            rx="1.26606"
            fill="#1865F2"
        />
        <path
            d="M51 27.14C51 30.40 48.35 33.05 45.09 33.05V21.23C48.35 21.23 51 23.87 51 27.14Z"
            fill="#BED5FF"
        />
        <rect
            width="4.00918"
            height="14.1376"
            rx="1.26606"
            transform="matrix(-1 0 0 1 45.3027 19.9629)"
            fill="#1865F2"
        />
    </StyledSvg>
);

const BeanieAndScarfGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M46.03 35.96L49.00 36.75"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <path
            d="M45.62 37.51L48.58 38.30"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <path
            d="M45.17 39.16L48.14 39.96"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <path
            d="M44.73 40.82L47.70 41.61"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <rect
            width="11.9264"
            height="5.82799"
            rx="0.602895"
            transform="matrix(-0.965926 -0.258819 -0.258819 0.965926 47.7423 36.0059)"
            fill="#AC4343"
        />
        <path
            d="M46.03 34.94L49.00 34.15"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <path
            d="M45.62 33.39L48.58 32.60"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <path
            d="M45.17 31.74L48.14 30.94"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <path
            d="M44.73 30.09L47.70 29.29"
            stroke="#FCB500"
            strokeWidth="0.80386"
            strokeLinecap="square"
        />
        <rect
            x="47.7423"
            y="34.8945"
            width="11.9264"
            height="5.82799"
            rx="0.602895"
            transform="rotate(165 47.7423 34.8945)"
            fill="#AC4343"
        />
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5777"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5777 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30548"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5776"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5776 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30548)">
            <ellipse
                cx="34.5754"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5754 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4176"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4176 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30548"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4176"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4176 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30548)">
            <ellipse
                cx="21.4154"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4154 34.4181)"
                fill="black"
            />
        </g>
        <path
            d="M12.63 20.23C12.63 14.60 17.19 10.05 22.81 10.05H33.40C39.02 10.05 43.57 14.60 43.57 20.23H12.63Z"
            fill="#FCB500"
        />
        <mask
            id="mask2_14505_30548"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="12"
            y="10"
            width="32"
            height="11"
        >
            <path
                d="M12.63 20.23C12.63 14.60 17.19 10.05 22.81 10.05H33.40C39.02 10.05 43.57 14.60 43.57 20.23H12.63Z"
                fill="#FCB500"
            />
        </mask>
        <g mask="url(#mask2_14505_30548)">
            <path
                d="M12.22 16.97H43.98"
                stroke="#F46A37"
                strokeWidth="1.42515"
            />
        </g>
        <rect
            x="11"
            y="17.5801"
            width="34"
            height="5.49701"
            rx="1.62874"
            fill="#FCB500"
        />
        <path d="M25.25 9.84H31.36" stroke="#F46A37" strokeWidth="1.01796" />
        <path
            d="M28.31 6.79L28.31 12.90"
            stroke="#F46A37"
            strokeWidth="1.01796"
        />
        <path
            d="M26.15 7.68L30.46 12.00"
            stroke="#F46A37"
            strokeWidth="1.01796"
        />
        <path
            d="M30.46 7.68L26.15 12.00"
            stroke="#F46A37"
            strokeWidth="1.01796"
        />
        <path
            d="M15.07 23.08L15.07 17.58"
            stroke="#F46A37"
            strokeWidth="1.42515"
        />
        <path
            d="M20.37 23.08L20.37 17.58"
            stroke="#F46A37"
            strokeWidth="1.42515"
        />
        <path
            d="M25.46 23.08L25.46 17.58"
            stroke="#F46A37"
            strokeWidth="1.42515"
        />
        <path
            d="M30.75 23.08L30.75 17.58"
            stroke="#F46A37"
            strokeWidth="1.42515"
        />
        <path
            d="M35.84 23.08L35.84 17.58"
            stroke="#F46A37"
            strokeWidth="1.42515"
        />
        <path
            d="M41.13 23.08L41.13 17.58"
            stroke="#F46A37"
            strokeWidth="1.42515"
        />
        <rect
            x="11.4021"
            y="36.1377"
            width="32.5563"
            height="5.82799"
            rx="0.602895"
            fill="#F46A37"
        />
    </StyledSvg>
);

const CrownGuideIcon = ({style}: {style?: StyleType}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5777"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5777 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30592"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5776"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5776 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30592)">
            <ellipse
                cx="34.5754"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5754 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4176"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4176 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30592"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4176"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4176 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30592)">
            <ellipse
                cx="21.4154"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4154 34.4181)"
                fill="black"
            />
        </g>
        <path
            d="M23.33 9.35L22.01 13.93C23.97 15.15 25.81 13.96 26.49 13.22L23.33 9.35Z"
            fill="#F28D31"
        />
        <path
            d="M33.11 9.35L34.43 13.93C32.48 15.15 30.63 13.96 29.95 13.22L33.11 9.35Z"
            fill="#F28D31"
        />
        <circle cx="23.4363" cy="9.14513" r="1.01818" fill="#F28D31" />
        <circle cx="33.2108" cy="9.14513" r="1.01818" fill="#F28D31" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M19.24 9.78L23.35 13.89L28.12 9.13H28.12L32.89 13.89L37.22 9.56V17.83C37.22 18.17 36.95 18.44 36.61 18.44H19.85C19.51 18.44 19.24 18.17 19.24 17.83V9.78Z"
            fill="#F8CA24"
        />
        <circle cx="19.2379" cy="9.77735" r="1.29981" fill="#F8CA24" />
        <circle cx="37.002" cy="9.77735" r="1.29981" fill="#F8CA24" />
        <circle cx="28.12" cy="8.47754" r="1.29981" fill="#F8CA24" />
        <ellipse
            cx="28.1199"
            cy="14.4396"
            rx="1.22182"
            ry="1.62909"
            fill="#5F1E5C"
        />
    </StyledSvg>
);

const BucketHatGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5778"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5778 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30612"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5778"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5778 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30612)">
            <ellipse
                cx="34.5756"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5756 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4178"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4178 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30612"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4177"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4177 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30612)">
            <ellipse
                cx="21.4155"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4155 34.4181)"
                fill="black"
            />
        </g>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.64 18.24V15.88C12.64 13.19 14.83 11 17.53 11H38.47C41.17 11 43.36 13.19 43.36 15.88V17.9L46 23.00H10L12.64 18.24Z"
            fill="#5BA26F"
        />
        <circle cx="36.2373" cy="16.0846" r="1.22034" fill="#0F3B21" />
        <circle cx="40.5085" cy="16.0846" r="1.22034" fill="#0F3B21" />
        <mask
            id="mask2_14505_30612"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="10"
            y="11"
            width="36"
            height="12"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.64 18.24V15.88C12.64 13.19 14.83 11 17.53 11H38.47C41.17 11 43.36 13.19 43.36 15.88V17.9L46 23.00H10L12.64 18.24Z"
                fill="#5BA26F"
            />
        </mask>
        <g mask="url(#mask2_14505_30612)">
            <path
                d="M10.61 18.93H45.19"
                stroke="#0F3B21"
                strokeWidth="1.42373"
            />
        </g>
    </StyledSvg>
);

const GraduationCapGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5778"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5778 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30633"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5778"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5778 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30633)">
            <ellipse
                cx="34.5756"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5756 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4178"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4178 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30633"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4177"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4177 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30633)">
            <ellipse
                cx="21.4155"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4155 34.4181)"
                fill="black"
            />
        </g>
        <path d="M47.26 12.79V22.77" stroke="#FCB500" strokeWidth="1.22182" />
        <rect
            x="16.3092"
            y="13.2012"
            width="24.4364"
            height="6.51636"
            fill="#010B1C"
        />
        <path
            d="M28.40 6.87L48.08 12.79L28.40 18.72L8.73 12.79L28.40 6.87Z"
            fill="#0B2149"
        />
        <path d="M44.21 23.59H50.32" stroke="#FCB500" strokeWidth="1.22182" />
        <path
            d="M47.26 20.53L47.26 26.64"
            stroke="#FCB500"
            strokeWidth="1.22182"
        />
        <path
            d="M45.10 21.42L49.42 25.74"
            stroke="#FCB500"
            strokeWidth="1.22182"
        />
        <path
            d="M49.42 21.42L45.10 25.74"
            stroke="#FCB500"
            strokeWidth="1.22182"
        />
    </StyledSvg>
);

const GlassesGuideIcon = ({style}: {style?: StyleType}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5779"
            cy="28.8581"
            r="6.01791"
            transform="rotate(-180 34.5779 28.8581)"
            fill="white"
        />
        <mask
            id="mask0_14505_30648"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="21"
            width="14"
            height="14"
        >
            <ellipse
                cx="34.5779"
                cy="28.4374"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5779 28.4374)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30648)">
            <ellipse
                cx="34.5757"
                cy="34.4171"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5757 34.4171)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4179"
            cy="28.8581"
            r="6.01791"
            transform="rotate(-180 21.4179 28.8581)"
            fill="white"
        />
        <mask
            id="mask1_14505_30648"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="21"
            width="14"
            height="14"
        >
            <ellipse
                cx="21.4178"
                cy="28.4374"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4178 28.4374)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30648)">
            <ellipse
                cx="21.4156"
                cy="34.4171"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4156 34.4171)"
                fill="black"
            />
        </g>
        <circle
            cx="21.3651"
            cy="28.9748"
            r="6.78219"
            stroke="#F46A37"
            strokeWidth="2.05521"
        />
        <circle
            cx="35.1349"
            cy="28.7693"
            r="6.57667"
            stroke="#F46A37"
            strokeWidth="2.05521"
        />
        <path d="M14.44 28.69H11.50" stroke="#F46A37" strokeWidth="2.94043" />
        <path d="M45.00 28.69H42.06" stroke="#F46A37" strokeWidth="2.94043" />
    </StyledSvg>
);

const CowboyHatGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5777"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5777 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30659"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5776"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5776 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30659)">
            <ellipse
                cx="34.5754"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5754 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4176"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4176 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30659"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4176"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4176 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30659)">
            <ellipse
                cx="21.4154"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4154 34.4181)"
                fill="black"
            />
        </g>
        <path
            d="M22.40 7.62L19.42 13.94C26.97 19.24 34.23 16.15 36.92 13.94L33.33 7.54C32.49 6.05 30.51 5.68 29.19 6.76L28.15 7.61C28.11 7.65 28.05 7.65 28.01 7.62L26.45 6.54C25.05 5.57 23.12 6.08 22.40 7.62Z"
            fill="#725622"
            stroke="#725622"
            strokeWidth="0.597185"
            strokeLinecap="square"
        />
        <path
            d="M42.90 13.89C45.06 11.87 42.29 9.53 39.50 10.52C35.98 11.77 31.97 12.48 27.71 12.48C23.46 12.48 19.45 11.77 15.93 10.52C13.14 9.53 10.36 11.87 12.53 13.89C16.25 17.36 21.68 19.55 27.71 19.55C33.75 19.55 39.17 17.36 42.90 13.89Z"
            fill="#725622"
        />
        <mask
            id="mask2_14505_30659"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="19"
            y="5"
            width="19"
            height="13"
        >
            <path
                d="M22.40 7.62L19.42 13.94C26.97 19.24 34.23 16.15 36.92 13.94L33.33 7.54C32.49 6.05 30.51 5.68 29.19 6.76L28.15 7.61C28.11 7.65 28.05 7.65 28.01 7.62L26.45 6.54C25.05 5.57 23.12 6.08 22.40 7.62Z"
                fill="#614613"
                stroke="#614613"
                strokeWidth="0.597185"
                strokeLinecap="square"
            />
        </mask>
        <g mask="url(#mask2_14505_30659)">
            <path
                d="M48.69 6.52C43.84 10.73 36.25 13.44 27.71 13.44C19.18 13.44 11.58 10.73 6.74 6.52"
                stroke="#DAB773"
                strokeWidth="2.64727"
            />
        </g>
    </StyledSvg>
);

const BeretGuideIcon = ({style}: {style?: StyleType}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5778"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5778 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30673"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5778"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5778 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30673)">
            <ellipse
                cx="34.5756"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5756 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4178"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4178 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30673"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4177"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4177 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30673)">
            <ellipse
                cx="21.4155"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4155 34.4181)"
                fill="black"
            />
        </g>
        <path
            d="M43.29 22.68C44.66 22.84 46.09 22.21 46.35 20.85C46.38 20.69 46.40 20.53 46.42 20.37C47.00 14.74 39.47 9.25 29.58 8.12C19.70 6.98 11.21 10.62 10.63 16.25C10.61 16.41 10.60 16.57 10.59 16.74C10.56 18.12 11.83 19.06 13.20 19.22L43.29 22.68Z"
            fill="#F46A37"
        />
        <path
            d="M29.64 10.39L33.43 7.18"
            stroke="#AC4343"
            strokeWidth="1.68727"
        />
    </StyledSvg>
);

const PartyHatGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5777"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 34.5777 28.859)"
            fill="white"
        />
        <mask
            id="mask0_14505_30682"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5776"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5776 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14505_30682)">
            <ellipse
                cx="34.5754"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5754 34.4181)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4176"
            cy="28.859"
            r="6.01791"
            transform="rotate(-180 21.4176 28.859)"
            fill="white"
        />
        <mask
            id="mask1_14505_30682"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4176"
                cy="28.4384"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4176 28.4384)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14505_30682)">
            <ellipse
                cx="21.4154"
                cy="34.4181"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4154 34.4181)"
                fill="black"
            />
        </g>
        <mask
            id="mask2_14505_30682"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="12"
            y="13"
            width="32"
            height="32"
        >
            <path
                d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
                fill="#DEAE93"
            />
        </mask>
        <g mask="url(#mask2_14505_30682)">
            <path
                d="M11.60 18.00L43.60 18.00"
                stroke="#F4EEE2"
                strokeWidth="1.2"
            />
        </g>
        <path d="M27.5 6.31L34 18.71H21L27.5 6.31Z" fill="#5F1E5C" />
        <mask
            id="mask3_14505_30682"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="21"
            y="6"
            width="13"
            height="13"
        >
            <path d="M27.5 6.31L34 18.71H21L27.5 6.31Z" fill="#5F1E5C" />
        </mask>
        <g mask="url(#mask3_14505_30682)">
            <path
                d="M29.02 9.21L22.08 15.68"
                stroke="#F4EEE2"
                strokeLinecap="square"
            />
            <path
                d="M32.93 10.06L21.01 21.02"
                stroke="#F4EEE2"
                strokeLinecap="square"
            />
            <path
                d="M35.35 12.47L23.43 23.42"
                stroke="#F4EEE2"
                strokeLinecap="square"
            />
        </g>
        <path d="M24.40 6.32H30.40" stroke="#F46A37" />
        <path d="M27.40 3.31L27.40 9.31" stroke="#F46A37" />
        <path d="M25.28 4.19L29.52 8.44" stroke="#F46A37" />
        <path d="M29.52 4.19L25.28 8.44" stroke="#F46A37" />
        <path
            d="M27.44 40.20C26.87 41.16 25.72 42.55 25.72 44.50M27.44 40.34H23.12C22.50 40.34 22 40.84 22 41.46V41.46C22 42.31 22.91 42.85 23.66 42.44L27.44 40.34Z"
            stroke="#F4EEE2"
            strokeWidth="1.2"
        />
        <path
            d="M27.16 40.20C27.73 41.16 28.88 42.55 28.88 44.50M27.16 40.34H31.48C32.10 40.34 32.6 40.84 32.6 41.46V41.46C32.6 42.31 31.69 42.85 30.94 42.44L27.16 40.34Z"
            stroke="#F4EEE2"
            strokeWidth="1.2"
        />
    </StyledSvg>
);

const FedoraShinyGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 30 31"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M6.79 12.94H23.25V23.97C23.25 25.14 21.86 25.75 21.00 24.95L20.10 24.12C19.85 23.89 19.53 23.76 19.19 23.76H8.12C7.39 23.76 6.79 23.16 6.79 22.42V12.94Z"
            fill="#DEAE93"
        />
        <circle
            cx="18.508"
            cy="17.1918"
            r="3.20955"
            transform="rotate(-180 18.508 17.1918)"
            fill="white"
        />
        <mask
            id="mask0_14639_11827"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="15"
            y="13"
            width="7"
            height="8"
        >
            <ellipse
                cx="18.508"
                cy="16.9675"
                rx="3.43392"
                ry="3.4339"
                transform="rotate(-180 18.508 16.9675)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_14639_11827)">
            <ellipse
                cx="18.5067"
                cy="20.1569"
                rx="3.11557"
                ry="3.11555"
                transform="rotate(-180 18.5067 20.1569)"
                fill="black"
            />
        </g>
        <circle
            cx="11.4892"
            cy="17.1918"
            r="3.20955"
            transform="rotate(-180 11.4892 17.1918)"
            fill="white"
        />
        <mask
            id="mask1_14639_11827"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="8"
            y="13"
            width="7"
            height="8"
        >
            <ellipse
                cx="11.4892"
                cy="16.9675"
                rx="3.43392"
                ry="3.4339"
                transform="rotate(-180 11.4892 16.9675)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_14639_11827)">
            <ellipse
                cx="11.4879"
                cy="20.1569"
                rx="3.11557"
                ry="3.11555"
                transform="rotate(-180 11.4879 20.1569)"
                fill="black"
            />
        </g>
        <path
            d="M6.53 2.29C6.57 2.19 6.71 2.19 6.75 2.29L7.19 3.44C7.20 3.47 7.22 3.49 7.25 3.50L8.22 3.94C8.31 3.99 8.31 4.12 8.22 4.16L7.25 4.60C7.22 4.61 7.20 4.64 7.19 4.66L6.75 5.82C6.71 5.92 6.57 5.92 6.53 5.82L6.09 4.66C6.08 4.64 6.05 4.61 6.03 4.60L5.06 4.16C4.96 4.12 4.96 3.99 5.06 3.94L6.03 3.50C6.05 3.49 6.08 3.47 6.09 3.44L6.53 2.29Z"
            fill="#FCB500"
            style={{transformOrigin: "6.57px 3.32px"}}
        >
            <animate
                attributeName="opacity"
                dur="1.1s"
                repeatCount="indefinite"
                values="0;1;0"
            />
            <animateTransform
                attributeName="transform"
                type="scale"
                dur="1.1s"
                begin="0s"
                repeatCount="indefinite"
                values="0.3;1;0.3"
            />
        </path>
        <path
            d="M3.30 6.62C3.34 6.52 3.48 6.52 3.52 6.62L3.78 7.31C3.79 7.34 3.81 7.36 3.84 7.38L4.42 7.64C4.51 7.68 4.51 7.81 4.42 7.85L3.84 8.12C3.81 8.13 3.79 8.15 3.78 8.18L3.52 8.87C3.48 8.97 3.34 8.97 3.30 8.87L3.04 8.18C3.02 8.15 3.00 8.13 2.97 8.12L2.40 7.85C2.30 7.81 2.30 7.68 2.40 7.64L2.97 7.38C3.00 7.36 3.02 7.34 3.04 7.31L3.30 6.62Z"
            fill="#FCB500"
            style={{transformOrigin: "3.34px 7.01px"}}
        >
            <animate
                attributeName="opacity"
                dur="1.1s"
                repeatCount="indefinite"
                values="0;0.5;1;0.5;0"
                begin="400ms"
            />
            <animateTransform
                attributeName="transform"
                type="scale"
                dur="1.1s"
                begin="400ms"
                repeatCount="indefinite"
                values="0;1;0"
            />
        </path>
        <path
            d="M23.48 2.99C23.52 2.89 23.66 2.89 23.70 2.99L23.96 3.68C23.98 3.71 24.00 3.73 24.03 3.74L24.60 4.01C24.70 4.05 24.70 4.18 24.60 4.22L24.03 4.48C24.00 4.49 23.98 4.52 23.96 4.55L23.70 5.23C23.66 5.33 23.52 5.33 23.48 5.23L23.22 4.55C23.21 4.52 23.19 4.49 23.16 4.48L22.58 4.22C22.49 4.18 22.49 4.05 22.58 4.01L23.16 3.74C23.19 3.73 23.21 3.71 23.22 3.68L23.48 2.99Z"
            fill="#FCB500"
            style={{transformOrigin: "23.53px 3.38px"}}
        >
            <animate
                attributeName="opacity"
                dur="1.1s"
                repeatCount="indefinite"
                values="1;0;1"
                begin="300ms"
            />
            <animateTransform
                attributeName="transform"
                type="scale"
                dur="1.1s"
                begin="300ms"
                repeatCount="indefinite"
                values="1;0;1"
            />
        </path>

        <path d="M4.87 13.11H25.13" stroke="black" strokeWidth="1.12593" />
        <path d="M4.87 13.11H25.13" stroke="black" strokeWidth="1.12593" />
        <path
            d="M9.12 7.83L7.01 12.54H23.22L22.67 9.00C22.35 6.97 20.21 5.78 18.32 6.58L16.27 7.45C16.21 7.47 16.15 7.47 16.09 7.45L13.24 6.22C11.66 5.54 9.82 6.26 9.12 7.83Z"
            fill="black"
            stroke="black"
            strokeWidth="0.562963"
            strokeLinecap="square"
            strokeLinejoin="round"
        />
        <mask
            id="mask2_14639_11827"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="6"
            y="5"
            width="18"
            height="8"
        >
            <path
                d="M9.12 7.83L7.01 12.54H23.22L22.67 9.00C22.35 6.97 20.21 5.78 18.32 6.58L16.27 7.45C16.21 7.47 16.15 7.47 16.09 7.45L13.24 6.22C11.66 5.54 9.82 6.26 9.12 7.83Z"
                fill="black"
                stroke="black"
                strokeWidth="0.562963"
                strokeLinecap="square"
                strokeLinejoin="round"
            />
        </mask>
        <g mask="url(#mask2_14639_11827)">
            <rect
                x="6.55554"
                y="10.9668"
                width="17.4519"
                height="1.5763"
                fill="#F4EEE2"
            />
        </g>
    </StyledSvg>
);

const DefaultGuideIcon = ({style}: {style?: StyleType}): React.ReactElement => (
    <StyledSvg
        style={style}
        preserveAspectRatio="xMidYMid"
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_14874_72680)">
            <path
                d="M17.24 13.34H38.83C41.69 13.34 44.02 15.67 44.02 18.53V42.02C44.02 44.29 41.32 45.47 39.66 43.93L37.91 42.31C37.43 41.86 36.80 41.61 36.14 41.61H14.65C13.21 41.61 12.05 40.45 12.05 39.02V18.53C12.05 15.67 14.38 13.34 17.24 13.34Z"
                fill="#DEAE93"
            />
            <circle
                cx="6.23284"
                cy="6.23284"
                r="6.23284"
                transform="matrix(1 0 0 -1 14.95 35.084)"
                fill="white"
            />
            <mask
                id="mask0_14874_72680"
                style={{maskType: "alpha"}}
                maskUnits="userSpaceOnUse"
                x="14"
                y="21"
                width="14"
                height="15"
            >
                <ellipse
                    cx="6.66855"
                    cy="6.66852"
                    rx="6.66855"
                    ry="6.66852"
                    transform="matrix(1 0 0 -1 14.5143 35.084)"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask0_14874_72680)">
                <ellipse
                    cx="6.05032"
                    cy="6.05029"
                    rx="6.05032"
                    ry="6.05029"
                    transform="matrix(1 0 0 -1 15.1349 40.6602)"
                    fill="black"
                />
            </g>
            <circle
                cx="6.23284"
                cy="6.23284"
                r="6.23284"
                transform="matrix(1 0 0 -1 28.58 35.084)"
                fill="white"
            />
            <mask
                id="mask1_14874_72680"
                style={{maskType: "alpha"}}
                maskUnits="userSpaceOnUse"
                x="28"
                y="21"
                width="14"
                height="15"
            >
                <ellipse
                    cx="6.66855"
                    cy="6.66852"
                    rx="6.66855"
                    ry="6.66852"
                    transform="matrix(1 0 0 -1 28.1443 35.084)"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask1_14874_72680)">
                <ellipse
                    cx="6.05032"
                    cy="6.05029"
                    rx="6.05032"
                    ry="6.05029"
                    transform="matrix(1 0 0 -1 28.7649 40.6602)"
                    fill="black"
                />
            </g>
        </g>
        <defs>
            <clipPath id="clip0_14874_72680">
                <rect
                    width="32"
                    height="32"
                    fill="white"
                    transform="translate(12 13)"
                />
            </clipPath>
        </defs>
    </StyledSvg>
);

const ReindeerGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.61 13.88H38.45C41.22 13.88 43.47 16.13 43.47 18.89V41.57C43.47 43.76 40.86 44.90 39.25 43.41L37.57 41.85C37.10 41.42 36.49 41.18 35.86 41.18H15.11C13.72 41.18 12.6 40.05 12.6 38.67V18.89C12.6 16.13 14.85 13.88 17.61 13.88Z"
            fill="#DEAE93"
        />
        <circle
            cx="34.5777"
            cy="28.86"
            r="6.01791"
            transform="rotate(-180 34.5777 28.86)"
            fill="white"
        />
        <mask
            id="mask0_16323_25181"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="28"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="34.5776"
                cy="28.4394"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 34.5776 28.4394)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask0_16323_25181)">
            <ellipse
                cx="34.5754"
                cy="34.4191"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 34.5754 34.4191)"
                fill="black"
            />
        </g>
        <circle
            cx="21.4176"
            cy="28.86"
            r="6.01791"
            transform="rotate(-180 21.4176 28.86)"
            fill="white"
        />
        <mask
            id="mask1_16323_25181"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="14"
            y="22"
            width="14"
            height="13"
        >
            <ellipse
                cx="21.4176"
                cy="28.4394"
                rx="6.4386"
                ry="6.43857"
                transform="rotate(-180 21.4176 28.4394)"
                fill="white"
            />
        </mask>
        <g mask="url(#mask1_16323_25181)">
            <ellipse
                cx="21.4154"
                cy="34.4191"
                rx="5.84169"
                ry="5.84166"
                transform="rotate(-180 21.4154 34.4191)"
                fill="black"
            />
        </g>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M26.45 31.03C26.42 31.03 26.39 31.03 26.36 31.04L26.35 31.02C26.30 31.02 26.30 31.02 26.27 31.01C26.18 31.03 26.15 31.09 26.12 31.15C26.10 31.19 26.08 31.23 26.05 31.26C26.04 31.20 26.04 31.17 26.03 31.13C25.96 31.14 25.93 31.17 25.93 31.23C25.90 31.25 25.87 31.26 25.84 31.27L25.84 31.27C25.80 31.28 25.75 31.30 25.71 31.31L25.70 31.35C25.69 31.39 25.67 31.42 25.66 31.47C25.60 31.48 25.54 31.49 25.46 31.51L25.49 31.56C25.50 31.58 25.51 31.60 25.52 31.62C25.45 31.64 25.38 31.67 25.31 31.69L25.25 31.72C25.25 31.71 25.25 31.72 25.25 31.72L25.25 31.76C25.23 31.77 25.22 31.77 25.21 31.78C25.18 31.79 25.15 31.80 25.13 31.82C25.11 31.85 25.1 31.88 25.09 31.92C25.08 31.93 25.07 31.95 25.07 31.97C25.05 31.97 25.01 31.98 24.97 31.99C24.95 32.12 24.88 32.21 24.77 32.29C24.76 32.30 24.75 32.31 24.74 32.32L24.74 32.32C24.72 32.34 24.70 32.35 24.69 32.37L24.68 32.37C24.65 32.39 24.64 32.40 24.62 32.40C24.55 32.47 24.55 32.53 24.56 32.60C24.56 32.64 24.57 32.69 24.55 32.73C24.54 32.70 24.52 32.68 24.51 32.66C24.50 32.65 24.49 32.64 24.48 32.63C24.43 32.67 24.42 32.72 24.45 32.77C24.42 32.81 24.39 32.85 24.36 32.89C24.34 32.91 24.32 32.93 24.31 32.96C24.31 32.98 24.32 33.00 24.32 33.03C24.33 33.05 24.34 33.08 24.34 33.11C24.32 33.13 24.29 33.16 24.27 33.19C24.25 33.21 24.23 33.23 24.21 33.25C24.25 33.28 24.28 33.30 24.31 33.31C24.27 33.36 24.23 33.41 24.20 33.46C24.18 33.49 24.16 33.51 24.14 33.54C24.14 33.53 24.14 33.54 24.14 33.54C24.15 33.56 24.16 33.57 24.16 33.58C24.16 33.59 24.15 33.60 24.14 33.61C24.12 33.64 24.10 33.66 24.10 33.69C24.10 33.72 24.11 33.75 24.12 33.79C24.12 33.81 24.12 33.82 24.13 33.84C24.12 33.86 24.09 33.88 24.06 33.92C24.12 34.03 24.11 34.15 24.07 34.27C24.06 34.31 24.05 34.36 24.04 34.40L24.04 34.40L24.03 34.41C24.02 34.45 24.02 34.45 24.01 34.47C23.98 34.56 24.03 34.61 24.07 34.67C24.10 34.70 24.12 34.73 24.14 34.77C24.10 34.76 24.07 34.76 24.05 34.75L24.02 34.74C24.00 34.81 24.02 34.85 24.08 34.87L24.07 35.11C24.10 35.14 24.13 35.17 24.17 35.20L24.17 35.20L24.19 35.21C24.18 35.27 24.17 35.32 24.17 35.38L24.16 35.41L24.28 35.40C24.27 35.46 24.27 35.53 24.27 35.59L24.27 35.59C24.27 35.62 24.27 35.65 24.27 35.68C24.27 35.68 24.27 35.69 24.27 35.69C24.28 35.69 24.29 35.69 24.30 35.70L24.30 35.70C24.30 35.70 24.31 35.70 24.31 35.70C24.31 35.71 24.31 35.73 24.31 35.74C24.31 35.77 24.31 35.80 24.33 35.83C24.34 35.85 24.37 35.88 24.39 35.90C24.41 35.91 24.42 35.92 24.44 35.94C24.44 35.95 24.43 35.96 24.43 35.98C24.43 35.99 24.43 36.01 24.43 36.04C24.48 36.06 24.52 36.11 24.55 36.16C24.59 36.20 24.61 36.26 24.63 36.32C24.65 36.36 24.67 36.41 24.68 36.45L24.69 36.45L24.68 36.45C24.68 36.46 24.69 36.47 24.69 36.48L24.69 36.48L24.69 36.48C24.69 36.50 24.69 36.51 24.69 36.53C24.73 36.61 24.79 36.63 24.86 36.65C24.90 36.66 24.94 36.67 24.97 36.69L24.93 36.71C24.90 36.72 24.88 36.72 24.86 36.73C24.88 36.80 24.92 36.82 24.98 36.81L25.10 37.00C25.14 37.01 25.20 37.02 25.25 37.02L25.26 37.03L25.26 37.03C25.29 37.09 25.31 37.14 25.34 37.20L25.35 37.19L25.35 37.19L25.35 37.19L25.35 37.19C25.38 37.16 25.40 37.14 25.43 37.12C25.46 37.18 25.49 37.23 25.53 37.29L25.53 37.29C25.55 37.31 25.56 37.33 25.58 37.36C25.57 37.35 25.58 37.36 25.58 37.36C25.59 37.36 25.60 37.35 25.60 37.35C25.61 37.35 25.61 37.35 25.62 37.35C25.62 37.36 25.63 37.37 25.64 37.38L25.64 37.38L25.64 37.38L25.64 37.38C25.66 37.41 25.67 37.43 25.70 37.45C25.72 37.46 25.76 37.47 25.79 37.47L25.79 37.47C25.81 37.47 25.83 37.47 25.85 37.48L25.86 37.50L25.86 37.50C25.87 37.52 25.88 37.54 25.89 37.56C25.95 37.56 26.01 37.57 26.06 37.59C26.11 37.61 26.16 37.65 26.21 37.69C26.23 37.70 26.25 37.71 26.27 37.72C26.29 37.73 26.31 37.74 26.32 37.75L26.33 37.75L26.32 37.76C26.36 37.80 26.36 37.80 26.37 37.82C26.44 37.88 26.51 37.86 26.57 37.84C26.61 37.82 26.65 37.81 26.69 37.81L26.62 37.91C26.67 37.95 26.71 37.96 26.75 37.91L26.81 37.94L26.81 37.94C26.86 37.96 26.91 37.98 26.96 38.01C27.00 37.99 27.04 37.97 27.09 37.95C27.12 37.96 27.14 37.98 27.17 38.00L27.17 38.00L27.17 38.00L27.17 38.00C27.20 38.01 27.23 38.03 27.26 38.05C27.27 38.02 27.27 38.00 27.28 37.97L27.28 37.97L27.29 37.94C27.32 37.95 27.34 37.96 27.36 37.98L27.36 37.98C27.42 38.01 27.47 38.04 27.54 38.07C27.53 38.07 27.54 38.07 27.54 38.07C27.55 38.06 27.55 38.05 27.56 38.05L27.56 38.05L27.57 38.04C27.58 38.04 27.59 38.05 27.60 38.05C27.63 38.06 27.66 38.08 27.68 38.08C27.71 38.08 27.74 38.06 27.78 38.05C27.79 38.04 27.81 38.04 27.83 38.03C27.84 38.04 27.86 38.06 27.89 38.08L27.91 38.09C28.01 38.01 28.12 38.01 28.24 38.03L28.27 38.03C28.30 38.04 28.34 38.04 28.37 38.04L28.38 38.04L28.38 38.04C28.42 38.05 28.43 38.05 28.44 38.06C28.54 38.08 28.58 38.02 28.63 37.97C28.65 37.94 28.68 37.91 28.72 37.89C28.71 37.92 28.71 37.95 28.70 37.97C28.70 37.99 28.70 38.00 28.70 38.01C28.76 38.02 28.80 38.00 28.82 37.94C28.85 37.94 28.87 37.94 28.90 37.94C28.95 37.94 29.00 37.94 29.04 37.93C29.07 37.90 29.09 37.86 29.12 37.82L29.13 37.80L29.17 37.80L29.17 37.80C29.22 37.81 29.27 37.81 29.32 37.81C29.32 37.77 29.31 37.73 29.30 37.70C29.39 37.69 29.48 37.69 29.58 37.68C29.57 37.69 29.58 37.68 29.58 37.68C29.58 37.67 29.59 37.66 29.59 37.65L29.59 37.64C29.60 37.64 29.61 37.64 29.62 37.64L29.62 37.64C29.65 37.64 29.69 37.64 29.71 37.62C29.73 37.60 29.75 37.57 29.78 37.54L29.78 37.54L29.78 37.54C29.79 37.53 29.80 37.52 29.81 37.50L29.83 37.50C29.85 37.50 29.88 37.51 29.91 37.51C29.96 37.40 30.05 37.33 30.17 37.28C30.18 37.28 30.19 37.27 30.21 37.27L30.21 37.27C30.24 37.25 30.26 37.24 30.29 37.23L30.29 37.22L30.29 37.23C30.34 37.21 30.34 37.21 30.37 37.21C30.45 37.17 30.46 37.11 30.48 37.04C30.49 37.00 30.50 36.96 30.52 36.92C30.54 36.97 30.55 37.00 30.56 37.04C30.63 37.01 30.65 36.97 30.63 36.91C30.67 36.88 30.71 36.86 30.74 36.83C30.77 36.81 30.79 36.79 30.82 36.78C30.82 36.74 30.82 36.71 30.82 36.66L30.82 36.66L30.82 36.66L30.82 36.66L30.83 36.62L30.89 36.59L30.89 36.59C30.92 36.57 30.96 36.55 31.00 36.52C30.98 36.50 30.96 36.49 30.95 36.47L30.95 36.47C30.94 36.46 30.93 36.45 30.92 36.44C30.97 36.40 31.03 36.36 31.09 36.32L31.09 36.32L31.09 36.32L31.14 36.27C31.14 36.27 31.14 36.27 31.14 36.27C31.14 36.26 31.14 36.25 31.14 36.24C31.13 36.24 31.13 36.24 31.13 36.23C31.14 36.22 31.15 36.21 31.17 36.21L31.17 36.21L31.17 36.21C31.19 36.19 31.21 36.17 31.22 36.15C31.23 36.11 31.24 36.08 31.24 36.04L31.24 36.04C31.24 36.02 31.24 36.01 31.24 35.99C31.25 35.98 31.26 35.98 31.28 35.97L31.28 35.97L31.28 35.97L31.28 35.97L31.28 35.97L31.28 35.97L31.28 35.97C31.29 35.96 31.31 35.95 31.33 35.94C31.31 35.81 31.36 35.71 31.43 35.60C31.43 35.59 31.44 35.57 31.45 35.56C31.47 35.54 31.48 35.51 31.50 35.48L31.50 35.48L31.18 35.37L31.18 35.37L31.50 35.48L31.50 35.47C31.53 35.44 31.53 35.44 31.55 35.42C31.60 35.34 31.57 35.28 31.55 35.21C31.53 35.17 31.52 35.14 31.52 35.09L31.54 35.11C31.57 35.13 31.59 35.15 31.61 35.16C31.65 35.11 31.65 35.06 31.60 35.02C31.62 34.96 31.64 34.91 31.66 34.85L31.68 34.80C31.67 34.77 31.65 34.74 31.63 34.71C31.62 34.70 31.61 34.68 31.6 34.66L31.61 34.65L31.61 34.65C31.63 34.59 31.65 34.54 31.68 34.48C31.64 34.47 31.60 34.47 31.57 34.46L31.57 34.46C31.60 34.40 31.61 34.34 31.63 34.27L31.63 34.27L31.63 34.27L31.63 34.27L31.63 34.27L31.63 34.27C31.64 34.25 31.65 34.22 31.66 34.19C31.66 34.20 31.66 34.19 31.66 34.19C31.65 34.18 31.64 34.17 31.63 34.17L31.63 34.17L31.63 34.17L31.63 34.17L31.63 34.16C31.63 34.15 31.64 34.14 31.64 34.13C31.65 34.09 31.66 34.06 31.65 34.04C31.65 34.00 31.63 33.98 31.61 33.94L31.61 33.94C31.60 33.93 31.59 33.91 31.58 33.90C31.59 33.88 31.60 33.86 31.61 33.83L31.61 33.83L31.62 33.81C31.58 33.76 31.55 33.71 31.53 33.65C31.52 33.60 31.51 33.53 31.51 33.47C31.51 33.42 31.50 33.38 31.50 33.33L31.50 33.33L31.50 33.33L31.51 33.30C31.51 33.28 31.51 33.27 31.51 33.25C31.51 33.16 31.45 33.12 31.39 33.09C31.36 33.06 31.32 33.04 31.30 33.01C31.35 33.00 31.38 33.00 31.41 33.00L31.41 33.00C31.42 32.93 31.38 32.90 31.32 32.89L31.30 32.81C31.28 32.76 31.27 32.72 31.26 32.67C31.22 32.65 31.17 32.63 31.12 32.61C31.11 32.54 31.09 32.48 31.08 32.41L31.08 32.41L31.08 32.41C31.04 32.43 31.01 32.45 30.98 32.46C30.96 32.39 30.94 32.33 30.92 32.26L30.92 32.26L30.92 32.26L30.92 32.26L30.92 32.26C30.91 32.23 30.91 32.21 30.90 32.19C30.90 32.19 30.90 32.18 30.89 32.18L30.86 32.18C30.85 32.17 30.85 32.16 30.84 32.14C30.83 32.11 30.82 32.09 30.80 32.07C30.78 32.04 30.75 32.03 30.72 32.02L30.72 32.02L30.72 32.02C30.70 32.01 30.68 32.00 30.67 32.00L30.66 31.97L30.66 31.97L30.66 31.97L30.66 31.97L30.66 31.97C30.66 31.95 30.66 31.93 30.65 31.90C30.59 31.89 30.54 31.86 30.49 31.82C30.45 31.79 30.41 31.74 30.37 31.69C30.35 31.67 30.34 31.64 30.32 31.62L30.32 31.61C30.32 31.60 30.31 31.59 30.31 31.58L30.31 31.58C30.30 31.56 30.29 31.56 30.29 31.54C30.23 31.47 30.16 31.47 30.09 31.47C30.05 31.48 30.00 31.48 29.96 31.46C29.98 31.44 30.00 31.43 30.02 31.42L30.02 31.42C30.03 31.41 30.05 31.40 30.06 31.39C30.02 31.33 29.97 31.32 29.92 31.35C29.86 31.30 29.80 31.25 29.73 31.20C29.69 31.21 29.64 31.21 29.58 31.23C29.53 31.18 29.49 31.14 29.44 31.08L29.41 31.11C29.40 31.14 29.38 31.16 29.37 31.18C29.30 31.11 29.23 31.06 29.15 31.00C29.15 31.00 29.15 31.00 29.15 31.00L29.15 31.00C29.13 31.00 29.12 31.01 29.11 31.02C29.10 31.01 29.09 31.00 29.08 30.99L29.08 30.99C29.05 30.97 29.03 30.95 29.00 30.95C28.97 30.94 28.93 30.94 28.88 30.95C28.87 30.95 28.85 30.95 28.84 30.96C28.83 30.94 28.81 30.93 28.79 30.91L28.79 30.91L28.77 30.88C28.65 30.93 28.54 30.90 28.42 30.86L28.41 30.85L28.37 30.84L28.37 30.84L28.36 30.84L28.37 30.78C28.33 30.76 28.33 30.75 28.31 30.73C28.23 30.70 28.17 30.73 28.12 30.77C28.08 30.80 28.05 30.82 28.00 30.83C28.02 30.78 28.04 30.75 28.05 30.72C27.99 30.69 27.95 30.70 27.92 30.75C27.87 30.73 27.82 30.73 27.76 30.72C27.74 30.72 27.72 30.71 27.70 30.71L27.69 30.72C27.66 30.74 27.62 30.77 27.58 30.80L27.55 30.79C27.50 30.77 27.46 30.76 27.40 30.74L27.40 30.79C27.40 30.81 27.39 30.84 27.39 30.86L27.32 30.84L27.32 30.84L27.32 30.84C27.26 30.83 27.19 30.82 27.12 30.80C27.13 30.80 27.12 30.80 27.12 30.80C27.11 30.81 27.10 30.82 27.10 30.83L27.10 30.83L27.10 30.83C27.09 30.83 27.08 30.83 27.07 30.83C27.04 30.82 27.01 30.82 26.98 30.83C26.95 30.84 26.93 30.86 26.90 30.88C26.88 30.89 26.87 30.90 26.85 30.91L26.83 30.90C26.81 30.90 26.79 30.89 26.76 30.88C26.68 30.98 26.57 31.01 26.45 31.03ZM29.15 31.00L29.15 31.00L29.15 31.00L29.15 31.00ZM31.14 36.27L31.14 36.28L31.14 36.27L31.14 36.27Z"
            fill="#F46A37"
        />
        <path
            d="M33.03 17.75C34.16 17.14 37.01 16.35 39.39 18.07C41.76 19.79 44.39 18.20 45.41 17.19"
            stroke="#816634"
            strokeWidth="2.51462"
            strokeLinecap="round"
        />
        <path
            d="M38.69 17.52C40.27 16.79 43.52 13.72 43.87 7.29"
            stroke="#816634"
            strokeWidth="2.51462"
            strokeLinecap="round"
        />
        <path
            d="M43.52 12.17C44.93 12.41 48.15 12.29 49.74 9.95"
            stroke="#816634"
            strokeWidth="2.51462"
            strokeLinecap="round"
        />
        <path
            d="M23.42 17.75C22.29 17.14 19.45 16.35 17.07 18.07C14.69 19.79 12.06 18.20 11.04 17.19"
            stroke="#816634"
            strokeWidth="2.51462"
            strokeLinecap="round"
        />
        <path
            d="M17.77 17.52C16.19 16.79 12.93 13.72 12.59 7.29"
            stroke="#816634"
            strokeWidth="2.51462"
            strokeLinecap="round"
        />
        <path
            d="M12.94 12.17C11.53 12.41 8.30 12.29 6.72 9.95"
            stroke="#816634"
            strokeWidth="2.51462"
            strokeLinecap="round"
        />
    </StyledSvg>
);

const OfflineGuideIcon = ({style}: {style?: StyleType}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g
            clipPath="url(#clip0_903_48461)"
            id="g8"
            transform="translate(12.28,13.00)"
        >
            <path
                d="M 5.24,0.34 H 26.83 c 2.87,0 5.19,2.33 5.19,5.19 V 29.02 c 0,2.27 -2.70,3.45 -4.36,1.90 L 25.90,29.31 C 25.43,28.86 24.80,28.61 24.14,28.61 H 2.65 c -1.43,0 -2.60,-1.16 -2.60,-2.60 V 5.53 c 0,-2.87 2.33,-5.19 5.19,-5.19 z"
                fill="#4caee5"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m 9.44,21.47 c -2.45,0 -4.44,-1.99 -4.44,-4.44 H 3.22 c 0,3.43 2.78,6.22 6.22,6.22 3.43,0 6.22,-2.78 6.22,-6.22 h -1.78 c 0,2.45 -1.99,4.44 -4.44,4.44 z"
                fill="#000000"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m 23.07,21.47 c -2.45,0 -4.44,-1.99 -4.44,-4.44 h -1.78 c 0,3.43 2.78,6.22 6.22,6.22 3.43,0 6.22,-2.78 6.22,-6.22 h -1.78 c 0,2.45 -1.99,4.44 -4.44,4.44 z"
                fill="#000000"
            />
        </g>
        <defs>
            <clipPath>
                <rect width="32" height="32" fill="#ffffff" />
            </clipPath>
        </defs>
    </StyledSvg>
);

const LookStraightAheadGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M18.24 13.34H39.83C42.70 13.34 45.02 15.67 45.02 18.53V42.02C45.02 44.29 42.32 45.47 40.66 43.93L38.91 42.31C38.43 41.86 37.80 41.61 37.14 41.61H15.65C14.21 41.61 13.05 40.45 13.05 39.02V18.53C13.05 15.67 15.38 13.34 18.24 13.34Z"
            fill="#DEAE93"
        />
        <circle
            cx="6.23284"
            cy="6.23284"
            r="6.23284"
            transform="matrix(1 0 0 -1 15.9492 35.084)"
            fill={color.white}
        />
        <mask
            id="mask0_5736_180435"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="15"
            y="22"
            width="14"
            height="14"
        >
            <circle
                cx="6.23284"
                cy="6.23284"
                r="6.23284"
                transform="matrix(1 0 0 -1 15.9492 35.084)"
                fill={color.white}
            />
        </mask>
        <g mask="url(#mask0_5736_180435)">
            <circle
                cx="3.98209"
                cy="3.98209"
                r="3.98209"
                transform="matrix(1 0 0 -1 18.1992 32.834)"
                fill="black"
            />
        </g>
        <circle cx="35.811" cy="28.852" r="6.23284" fill={color.white} />
        <mask
            id="mask1_5736_180435"
            style={{maskType: "alpha"}}
            maskUnits="userSpaceOnUse"
            x="29"
            y="22"
            width="14"
            height="14"
        >
            <circle cx="35.811" cy="28.852" r="6.23284" fill={color.white} />
        </mask>
        <g mask="url(#mask1_5736_180435)">
            <circle cx="35.8102" cy="28.8512" r="3.98209" fill="black" />
        </g>
    </StyledSvg>
);

const HeartEyesGuideIcon = ({
    style,
}: {
    style?: StyleType;
}): React.ReactElement => (
    <StyledSvg
        style={style}
        viewBox="0 0 56 56"
        preserveAspectRatio="xMidYMid"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g
            clipPath="url(#clip0_2465_3273)"
            transform="translate(12, 13) scale(0.57)"
        >
            <path
                d="M8.94 0.42H46.74C51.77 0.42 55.84 4.49 55.84 9.51V50.66C55.84 54.63 51.11 56.69 48.20 53.99L45.13 51.15C44.29 50.37 43.19 49.94 42.04 49.94H4.39C1.88 49.94 -0.16 47.90 -0.16 45.39V9.51C-0.16 4.49 3.91 0.42 8.94 0.42Z"
                fill="#DEAE93"
            />
            <circle cx="8.94011" cy="36.2123" r="7.93974" fill="#FBB1D4" />
            <circle cx="46.6539" cy="36.2123" r="7.93974" fill="#FBB1D4" />
            <path
                d="M26.59 25.87C26.67 26.65 26.59 27.43 26.36 28.18C26.12 28.93 25.75 29.62 25.25 30.22L16.99 40.33C16.92 40.41 16.84 40.48 16.74 40.53C16.65 40.59 16.54 40.62 16.43 40.63C16.32 40.64 16.21 40.63 16.11 40.59C16.01 40.56 15.91 40.51 15.83 40.44L5.84 32.04C4.62 31.03 3.86 29.58 3.71 28.01C3.56 26.44 4.05 24.87 5.05 23.66C6.06 22.44 7.51 21.67 9.08 21.53C10.65 21.38 12.22 21.86 13.43 22.87L14.88 23.98L16.10 22.61C16.85 21.71 17.85 21.05 18.98 20.72C20.11 20.38 21.31 20.39 22.43 20.74C23.55 21.09 24.55 21.76 25.29 22.67C26.03 23.59 26.48 24.70 26.59 25.87Z"
                fill="#EB4E3B"
            />
            <path
                d="M28.98 25.87C28.91 26.65 28.99 27.43 29.22 28.18C29.45 28.93 29.82 29.62 30.32 30.22L38.58 40.33C38.65 40.41 38.73 40.48 38.83 40.53C38.93 40.59 39.03 40.62 39.14 40.63C39.25 40.64 39.36 40.63 39.46 40.59C39.57 40.56 39.66 40.51 39.74 40.44L49.73 32.04C50.95 31.03 51.71 29.58 51.86 28.01C52.01 26.44 51.53 24.87 50.52 23.66C49.51 22.44 48.06 21.67 46.49 21.53C44.92 21.38 43.36 21.86 42.14 22.87L40.70 23.98L39.48 22.61C38.72 21.71 37.72 21.05 36.59 20.72C35.46 20.38 34.26 20.39 33.14 20.74C32.02 21.09 31.02 21.76 30.28 22.67C29.54 23.59 29.09 24.70 28.98 25.87Z"
                fill="#EB4E3B"
            />
        </g>
        <defs>
            <clipPath id="clip0_2465_3273">
                <rect width="56" height="56" fill="white" />
            </clipPath>
        </defs>
    </StyledSvg>
);

export {GuideIcon32};
